import { useState, useEffect, useMemo, useCallback } from "react";
import { useApp } from "../context/AppContext";

function EditableCell({ value, onSave, style, type = "text" }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value ?? "");

  const commit = () => {
    setEditing(false);
    const newVal = type === "number" ? (parseFloat(val) || 0) : val;
    if (String(newVal) !== String(value ?? "")) onSave(newVal);
  };

  if (!editing) {
    return (
      <span onClick={() => { setVal(value ?? ""); setEditing(true); }} style={{ ...style, cursor: "text", minWidth: 30, display: "inline-block" }}>
        {value || "—"}
      </span>
    );
  }

  return (
    <input
      autoFocus
      type={type}
      value={val}
      onChange={e => setVal(e.target.value)}
      onBlur={commit}
      onKeyDown={e => { if (e.key === "Enter") commit(); if (e.key === "Escape") setEditing(false); }}
      style={{ fontSize: 12, padding: "2px 4px", border: "1px solid #6366f1", borderRadius: 4, outline: "none", width: type === "number" ? 80 : 120, background: "transparent", color: "inherit", fontFamily: "inherit" }}
    />
  );
}

export default function FinanceView() {
  const { apiFetch, dark, t, card, flash } = useApp();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editProjectId, setEditProjectId] = useState(null);
  const [planMonth, setPlanMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [plans, setPlans] = useState({});

  const loadPlans = async (month) => {
    try {
      const r = await apiFetch(`/api/finance/plans?month=${month}`);
      const d = await r.json();
      if (Array.isArray(d)) {
        const map = {};
        d.forEach(p => { map[p.category] = parseFloat(p.plan_amount) || 0; });
        setPlans(map);
      }
    } catch {}
  };

  useEffect(() => { loadPlans(planMonth); }, [planMonth]);

  const savePlan = async (category, amount) => {
    try {
      await apiFetch("/api/finance/plans", {
        method: "PUT",
        body: JSON.stringify({ month: planMonth, category, amount }),
      });
      setPlans(prev => ({ ...prev, [category]: parseFloat(amount) || 0 }));
    } catch (e) { flash("Помилка: " + e.message); }
  };

  const load = async () => {
    try {
      const r = await apiFetch("/api/finance");
      const d = await r.json();
      if (Array.isArray(d)) setRecords(d);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const togglePaid = async (id) => {
    try {
      const r = await apiFetch(`/api/finance/toggle-paid/${id}`, { method: "POST" });
      const d = await r.json();
      if (d.ok) {
        setRecords(prev => prev.map(rec =>
          rec.id === id ? { ...rec, status: d.status, paid_at: d.paid_at } : rec
        ));
      }
    } catch (e) { flash("Помилка: " + e.message); }
  };

  const updateField = useCallback(async (id, field, value) => {
    try {
      await apiFetch(`/api/finance/${id}`, { method: "PUT", body: JSON.stringify({ [field]: value }) });
      setRecords(prev => prev.map(rec => rec.id === id ? { ...rec, [field]: value } : rec));
    } catch (e) { flash("Помилка: " + e.message); }
  }, [apiFetch, flash]);

  const openInvoice = async (projectId, periodStart, periodEnd) => {
    try {
      const ps = new Date(periodStart).toISOString().slice(0, 10);
      const pe = new Date(periodEnd).toISOString().slice(0, 10);
      const r = await apiFetch(`/api/projects/${projectId}/invoices/generate`, {
        method: "POST",
        body: JSON.stringify({ periodStart: ps, periodEnd: pe }),
      });
      const d = await r.json();
      if (d.error) { flash("Помилка: " + d.error); return; }
      const win = window.open("", "_blank");
      win.document.write(d.html);
      win.document.close();
    } catch (e) { flash("Помилка: " + e.message); }
  };

  const downloadInvoice = async (projectId, periodStart, periodEnd) => {
    try {
      const ps = new Date(periodStart).toISOString().slice(0, 10);
      const pe = new Date(periodEnd).toISOString().slice(0, 10);
      const r = await apiFetch(`/api/projects/${projectId}/invoices/generate`, {
        method: "POST",
        body: JSON.stringify({ periodStart: ps, periodEnd: pe }),
      });
      const d = await r.json();
      if (d.error) { flash("Помилка: " + d.error); return; }
      const win = window.open("", "_blank");
      win.document.write(d.html);
      win.document.close();
      // Trigger print from parent context (avoids CSP inline-script blocking)
      const tryPrint = () => { try { win.focus(); win.print(); } catch {} };
      if (win.document.readyState === "complete") setTimeout(tryPrint, 300);
      else win.addEventListener("load", () => setTimeout(tryPrint, 300));
    } catch (e) { flash("Помилка: " + e.message); }
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString("uk-UA", { day: "2-digit", month: "2-digit" }) : "—";
  const fmtMonth = (d) => d ? `${String(new Date(d).getMonth() + 1).padStart(2, "0")}.${String(new Date(d).getFullYear()).slice(2)}` : "—";
  const fmtMoney = (n) => n ? Number(n).toLocaleString("uk-UA") : "—";

  // LTV per project: month number (1, 2, 3...) by period order
  const ltvMap = useMemo(() => {
    const byProject = {};
    records.forEach(r => {
      if (!byProject[r.project_id]) byProject[r.project_id] = [];
      byProject[r.project_id].push(r);
    });
    const result = {};
    for (const [pid, recs] of Object.entries(byProject)) {
      const periods = [...new Set(recs.map(r => r.period_start))].sort();
      periods.forEach((period, i) => {
        recs.filter(r => r.period_start === period).forEach(r => {
          result[r.id] = i + 1;
        });
      });
    }
    return result;
  }, [records]);

  // Підсумки
  const totals = useMemo(() => {
    let totalAmount = 0, totalPaid = 0, totalUnpaid = 0;
    records.forEach(r => {
      totalAmount += Number(r.amount) || 0;
      if (r.status === "paid") totalPaid += Number(r.amount) || 0;
      else totalUnpaid += Number(r.amount) || 0;
    });
    return { totalAmount, totalPaid, totalUnpaid, debitorka: totalAmount - totalPaid };
  }, [records]);

  // Факт за обраний місяць по категоріях
  const factByCategory = useMemo(() => {
    const map = {};
    records.forEach(r => {
      if (!r.period_start) return;
      const d = new Date(r.period_start);
      const m = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      if (m !== planMonth) return;
      const cat = r.category || "Інше";
      map[cat] = (map[cat] || 0) + (Number(r.amount) || 0);
    });
    return map;
  }, [records, planMonth]);

  const PLAN_CATEGORIES = ["База", "Копірайтинг", "Рейт. посилання", "Розробка", "Крауд", "ПФ"];
  const planTotals = useMemo(() => {
    let plan = 0, fact = 0;
    PLAN_CATEGORIES.forEach(c => {
      plan += plans[c] || 0;
      fact += factByCategory[c] || 0;
    });
    return { plan, fact };
  }, [plans, factByCategory]);

  const thS = {
    padding: "10px 12px", fontSize: 11, fontWeight: 700, textTransform: "uppercase",
    letterSpacing: 0.5, textAlign: "left", whiteSpace: "nowrap",
    borderBottom: `2px solid ${dark ? "#333" : "#e5e7eb"}`,
    background: dark ? "#1a1a24" : "#f8f9fb", color: t.textMuted,
    position: "sticky", top: 0, zIndex: 1,
  };
  const tdS = {
    padding: "8px 12px", fontSize: 12, borderBottom: `1px solid ${dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)"}`,
    whiteSpace: "nowrap",
  };

  if (loading) return <div style={{ textAlign: "center", padding: 40, color: t.textMuted }}>Завантаження...</div>;

  return (
    <div>
      <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 16 }}>Фінансова таблиця</h2>

      {/* Підсумки */}
      <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        {[
          ["Загалом", totals.totalAmount, "#6366f1"],
          ["Оплачено", totals.totalPaid, "#10b981"],
          ["Не оплачено", totals.totalUnpaid, "#ef4444"],
          ["Дебіторка", totals.debitorka, "#f59e0b"],
        ].map(([label, val, color]) => (
          <div key={label} style={{ ...card, margin: 0, padding: "12px 20px", minWidth: 140, borderLeft: `4px solid ${color}` }}>
            <div style={{ fontSize: 10, color: t.textMuted, fontWeight: 600, marginBottom: 2 }}>{label}</div>
            <div style={{ fontSize: 18, fontWeight: 700, color }}>{fmtMoney(val)} грн</div>
          </div>
        ))}
      </div>

      {/* План vs Факт по місяцю */}
      <div style={{ ...card, margin: "0 0 16px", padding: 16 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
          <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>📊 План vs Факт</h3>
          <input type="month" value={planMonth} onChange={e => setPlanMonth(e.target.value)}
            style={{ padding: "6px 10px", borderRadius: 8, border: `1px solid ${t.border}`, background: dark ? "#1e1e2c" : "#fff", color: t.text, fontSize: 12, fontFamily: "inherit" }} />
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={{ textAlign: "left", padding: "8px 10px", fontSize: 11, fontWeight: 700, color: t.textMuted, borderBottom: `1px solid ${t.border}` }}>Категорія</th>
              <th style={{ textAlign: "right", padding: "8px 10px", fontSize: 11, fontWeight: 700, color: t.textMuted, borderBottom: `1px solid ${t.border}` }}>План</th>
              <th style={{ textAlign: "right", padding: "8px 10px", fontSize: 11, fontWeight: 700, color: t.textMuted, borderBottom: `1px solid ${t.border}` }}>Факт</th>
              <th style={{ textAlign: "right", padding: "8px 10px", fontSize: 11, fontWeight: 700, color: t.textMuted, borderBottom: `1px solid ${t.border}` }}>Різниця</th>
              <th style={{ textAlign: "left", padding: "8px 10px", fontSize: 11, fontWeight: 700, color: t.textMuted, borderBottom: `1px solid ${t.border}`, width: "30%" }}>Виконано</th>
            </tr>
          </thead>
          <tbody>
            {PLAN_CATEGORIES.map(cat => {
              const plan = plans[cat] || 0;
              const fact = factByCategory[cat] || 0;
              const diff = fact - plan;
              const pct = plan > 0 ? Math.min(100, (fact / plan) * 100) : 0;
              return (
                <tr key={cat}>
                  <td style={{ padding: "10px", fontSize: 13, fontWeight: 600, borderBottom: `1px solid ${dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)"}` }}>{cat}</td>
                  <td style={{ padding: "6px 10px", textAlign: "right", borderBottom: `1px solid ${dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)"}` }}>
                    <EditableCell value={plan} onSave={v => savePlan(cat, v)} type="number" style={{ color: "#6366f1", fontWeight: 600 }} />
                  </td>
                  <td style={{ padding: "10px", textAlign: "right", fontSize: 13, fontWeight: 600, color: "#10b981", borderBottom: `1px solid ${dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)"}` }}>{fmtMoney(fact)}</td>
                  <td style={{ padding: "10px", textAlign: "right", fontSize: 13, fontWeight: 600, color: diff >= 0 ? "#10b981" : "#ef4444", borderBottom: `1px solid ${dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)"}` }}>
                    {diff >= 0 ? "+" : ""}{fmtMoney(diff)}
                  </td>
                  <td style={{ padding: "10px", borderBottom: `1px solid ${dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)"}` }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{ flex: 1, height: 6, background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)", borderRadius: 3, overflow: "hidden" }}>
                        <div style={{ width: `${pct}%`, height: "100%", background: pct >= 90 ? "#10b981" : pct >= 50 ? "#f59e0b" : "#6366f1", transition: "width .3s" }} />
                      </div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, minWidth: 36, textAlign: "right" }}>{Math.round(pct)}%</div>
                    </div>
                  </td>
                </tr>
              );
            })}
            <tr style={{ background: dark ? "rgba(99,102,241,0.06)" : "rgba(99,102,241,0.04)" }}>
              <td style={{ padding: "12px 10px", fontSize: 13, fontWeight: 700 }}>Разом</td>
              <td style={{ padding: "12px 10px", textAlign: "right", fontSize: 14, fontWeight: 700, color: "#6366f1" }}>{fmtMoney(planTotals.plan)}</td>
              <td style={{ padding: "12px 10px", textAlign: "right", fontSize: 14, fontWeight: 700, color: "#10b981" }}>{fmtMoney(planTotals.fact)}</td>
              <td style={{ padding: "12px 10px", textAlign: "right", fontSize: 14, fontWeight: 700, color: (planTotals.fact - planTotals.plan) >= 0 ? "#10b981" : "#ef4444" }}>
                {(planTotals.fact - planTotals.plan) >= 0 ? "+" : ""}{fmtMoney(planTotals.fact - planTotals.plan)}
              </td>
              <td style={{ padding: "12px 10px", fontSize: 12, fontWeight: 700, color: t.textMuted }}>
                {planTotals.plan > 0 ? Math.round((planTotals.fact / planTotals.plan) * 100) : 0}%
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {records.length === 0 ? (
        <div style={{ ...card, textAlign: "center", padding: 40, color: t.textMuted }}>
          Немає записів. Записи з'являться після завантаження рахунків менеджером.
        </div>
      ) : (
        <div style={{ ...card, margin: 0, padding: 0, overflow: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1250 }}>
            <thead>
              <tr>
                <th style={thS}>Дата старту</th>
                <th style={thS}>Місяць</th>
                <th style={thS}>Проєкт</th>
                <th style={thS}>Відправник</th>
                <th style={thS}>Отримувач</th>
                <th style={thS}>Розподіл</th>
                <th style={{ ...thS, textAlign: "right" }}>Сума</th>
                <th style={{ ...thS, textAlign: "right" }}>Сума оплати</th>
                <th style={thS}>Дата оплати</th>
                <th style={thS}>Місяць оплати</th>
                <th style={{ ...thS, textAlign: "right" }}>LTV</th>
                <th style={thS}>Рахунок</th>
                <th style={thS}>Статус</th>
              </tr>
            </thead>
            <tbody>
              {records.map(rec => {
                const isPaid = rec.status === "paid";
                return (
                  <tr key={rec.id} style={{
                    background: isPaid ? (dark ? "rgba(16,185,129,0.04)" : "rgba(16,185,129,0.02)") : "transparent",
                    transition: "background .15s",
                  }}>
                    <td style={tdS}>{fmtDate(rec.period_start)}</td>
                    <td style={tdS}>{fmtMonth(rec.period_start)}</td>
                    <td style={tdS}>
                      <span style={{ fontWeight: 600 }}>{rec.domain || rec.project_name || rec.client || "—"}</span>
                      {!rec.is_new_sales && rec.project_id && (
                        <button onClick={() => setEditProjectId(rec.project_id)} title="Налаштування проєкту"
                          style={{ marginLeft: 6, background: "none", border: "none", cursor: "pointer", fontSize: 12, color: t.textMuted, padding: 0 }}>⚙️</button>
                      )}
                      {rec.is_new_sales && <span style={{ marginLeft: 6, fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 4, background: "rgba(168,85,247,0.12)", color: "#a855f7" }}>New Sales</span>}
                    </td>
                    <td style={{ ...tdS, fontSize: 11 }}>
                      <EditableCell value={rec.supplier} onSave={v => updateField(rec.id, "supplier_name", v)} />
                    </td>
                    <td style={{ ...tdS, fontSize: 11 }}>
                      <EditableCell value={rec.client} onSave={v => updateField(rec.id, "client_name", v)} />
                    </td>
                    <td style={tdS}>
                      <EditableCell value={rec.category} onSave={v => updateField(rec.id, "category", v)} />
                    </td>
                    <td style={{ ...tdS, textAlign: "right" }}>
                      <EditableCell value={rec.amount} onSave={v => updateField(rec.id, "amount", v)} type="number" style={{ fontWeight: 600 }} />
                    </td>
                    <td style={{ ...tdS, textAlign: "right", fontWeight: 600, color: isPaid ? "#10b981" : t.textMuted }}>
                      {isPaid ? fmtMoney(rec.amount) : "—"}
                    </td>
                    <td style={tdS}>{isPaid && rec.paid_at ? fmtDate(rec.paid_at) : "—"}</td>
                    <td style={tdS}>{isPaid && rec.paid_at ? fmtMonth(rec.paid_at) : "—"}</td>
                    <td style={{ ...tdS, textAlign: "center", fontWeight: 700, color: "#8b5cf6" }}>
                      {ltvMap[rec.id] || 1}
                    </td>
                    <td style={tdS}>
                      {rec.invoice_id ? (
                        <div style={{ display: "inline-flex", gap: 2 }}>
                          <button onClick={() => openInvoice(rec.project_id, rec.period_start, rec.period_end)} style={{
                            background: "none", border: "none", cursor: "pointer", fontSize: 14, color: "#6366f1", padding: "2px 6px",
                          }} title="Відкрити рахунок">📄</button>
                          <button onClick={() => downloadInvoice(rec.project_id, rec.period_start, rec.period_end)} style={{
                            background: "none", border: "none", cursor: "pointer", fontSize: 14, color: "#10b981", padding: "2px 6px",
                          }} title="Завантажити PDF">⬇</button>
                        </div>
                      ) : <span style={{ fontSize: 10, color: t.textMuted }}>—</span>}
                    </td>
                    <td style={tdS}>
                      <button onClick={() => togglePaid(rec.id)} style={{
                        padding: "4px 14px", borderRadius: 6, fontSize: 11, fontWeight: 700,
                        cursor: "pointer", border: "none", transition: "all .15s",
                        background: isPaid ? "rgba(16,185,129,0.12)" : "rgba(239,68,68,0.1)",
                        color: isPaid ? "#10b981" : "#ef4444",
                      }}>
                        {isPaid ? "Закрито" : "Не оплачено"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editProjectId && <FinanceProjectModal projectId={editProjectId} onClose={() => setEditProjectId(null)} apiFetch={apiFetch} dark={dark} t={t} card={card} flash={flash} />}
    </div>
  );
}

function FinanceProjectModal({ projectId, onClose, apiFetch, dark, t, card, flash }) {
  const [proj, setProj] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState(0);
  const [ctab, setCtab] = useState(0);
  const [invoices, setInvoices] = useState([]);

  const loadInvoices = async () => {
    try {
      const r = await apiFetch(`/api/projects/${projectId}/invoices`);
      if (r.ok) { const d = await r.json(); if (Array.isArray(d)) setInvoices(d); }
    } catch {}
  };

  useEffect(() => {
    (async () => {
      try {
        const r = await apiFetch(`/api/projects?limit=200`);
        const d = await r.json();
        const list = d.projects || d;
        const p = list.find(x => x.id === projectId);
        if (!p) { flash("Проєкт не знайдено"); onClose(); return; }
        setProj(p);
        loadInvoices();
        const sList = p.suppliersList?.length ? p.suppliersList : (p.supplierName ? [{ name: p.supplierName, details: p.supplierDetails || "" }] : []);
        const cList = p.clientsList?.length ? p.clientsList : (p.clientName ? [{ name: p.clientName, details: p.clientDetails || "" }] : []);
        setForm({
          startDate: p.startDate || "",
          payment: p.payment || "",
          lang: p.lang || "",
          budget: { dev: "", links: "", crowd: "", copy: "", pf: "", ...(p.budget || {}) },
          suppliersList: sList,
          clientsList: cList,
          welcomeEmail: p.welcomeEmail || "",
        });
      } catch (e) { flash("Помилка: " + e.message); onClose(); }
    })();
  }, [projectId]);

  const save = async () => {
    setSaving(true);
    try {
      const supActive = form.suppliersList[tab] || { name: "", details: "" };
      const cliActive = form.clientsList[ctab] || { name: "", details: "" };
      const body = {
        startDate: form.startDate,
        payment: form.payment,
        lang: form.lang,
        budget: form.budget,
        suppliersList: form.suppliersList,
        clientsList: form.clientsList,
        supplierName: supActive.name,
        supplierDetails: supActive.details,
        clientName: cliActive.name,
        clientDetails: cliActive.details,
        welcomeEmail: form.welcomeEmail,
      };
      const r = await apiFetch(`/api/projects/${projectId}`, { method: "PUT", body: JSON.stringify(body) });
      if (r.ok) { flash("✓ Збережено"); onClose(); }
      else { const d = await r.json().catch(() => ({})); flash("Помилка: " + (d.error || "не вдалося")); }
    } catch (e) { flash("Помилка: " + e.message); }
    finally { setSaving(false); }
  };

  if (!form) return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }}>
      <div style={{ color: "#fff" }}>Завантаження...</div>
    </div>
  );

  const inp = { width: "100%", padding: "8px 10px", borderRadius: 8, border: `1px solid ${t.border}`, background: dark ? "#1e1e2c" : "#fff", color: t.text, fontSize: 13, fontFamily: "inherit", boxSizing: "border-box", outline: "none" };
  const lbl = { fontSize: 10, color: t.textMuted, fontWeight: 600, marginBottom: 4, display: "block", textTransform: "uppercase", letterSpacing: 0.5 };
  const tabStyle = (active, color = "#10b981") => ({
    padding: "5px 10px", fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 6,
    border: `1.5px solid ${active ? color : t.border}`,
    background: active ? `${color}1f` : "transparent", color: active ? color : t.textMuted,
  });

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, padding: 20 }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ background: dark ? "#14142b" : "#fff", borderRadius: 16, padding: 24, maxWidth: 720, width: "100%", maxHeight: "90vh", overflowY: "auto", border: `1px solid ${t.border}` }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>⚙️ {proj.name}</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 22, cursor: "pointer", color: t.textMuted }}>×</button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 16 }}>
          <div>
            <label style={lbl}>📅 Старт</label>
            <input type="date" value={form.startDate ? new Date(form.startDate).toISOString().slice(0, 10) : ""} onChange={e => setForm(f => ({ ...f, startDate: e.target.value }))} style={inp} />
          </div>
          <div>
            <label style={lbl}>💰 Сума</label>
            <input value={form.payment} onChange={e => setForm(f => ({ ...f, payment: e.target.value }))} style={inp} />
          </div>
          <div>
            <label style={lbl}>🌐 Мова</label>
            <div style={{ display: "flex", gap: 4 }}>
              {["UKR", "RU", "ENG"].map(l => (
                <button key={l} onClick={() => setForm(f => ({ ...f, lang: l }))} style={{ flex: 1, padding: "8px 0", fontSize: 11, fontWeight: 700, border: `1.5px solid ${form.lang === l ? "#6366f1" : t.border}`, background: form.lang === l ? "rgba(99,102,241,0.12)" : "transparent", color: form.lang === l ? "#6366f1" : t.textMuted, borderRadius: 6, cursor: "pointer" }}>{l}</button>
              ))}
            </div>
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={lbl}>📊 Бюджет</label>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr", gap: 8 }}>
            {[["dev", "Розробка"], ["links", "Посилання"], ["crowd", "Крауд"], ["copy", "Копірайт"], ["pf", "ПФ"]].map(([k, lab]) => (
              <div key={k}>
                <div style={{ fontSize: 10, color: t.textMuted, marginBottom: 3 }}>{lab}</div>
                <input value={form.budget[k] || ""} onChange={e => setForm(f => ({ ...f, budget: { ...f.budget, [k]: e.target.value } }))} style={{ ...inp, fontSize: 12 }} />
              </div>
            ))}
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={lbl}>📤 Відправник (постачальник)</label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginBottom: 6 }}>
            {form.suppliersList.map((s, i) => (
              <div key={i} onClick={() => setTab(i)} style={tabStyle(tab === i, "#6366f1")}>
                {s.name || `#${i + 1}`}
                <span onClick={e => { e.stopPropagation(); setForm(f => ({ ...f, suppliersList: f.suppliersList.filter((_, j) => j !== i) })); if (tab >= i && tab > 0) setTab(tab - 1); }} style={{ marginLeft: 6, cursor: "pointer", opacity: 0.6 }}>×</span>
              </div>
            ))}
            <button onClick={() => { setForm(f => ({ ...f, suppliersList: [...f.suppliersList, { name: "", details: "" }] })); setTab(form.suppliersList.length); }} style={{ padding: "5px 10px", fontSize: 11, fontWeight: 700, border: `1.5px dashed ${t.border}`, background: "transparent", color: t.textMuted, borderRadius: 6, cursor: "pointer" }}>+ Додати</button>
          </div>
          {form.suppliersList[tab] && (
            <>
              <input value={form.suppliersList[tab].name} onChange={e => { const v = e.target.value; setForm(f => { const list = [...f.suppliersList]; list[tab] = { ...list[tab], name: v }; return { ...f, suppliersList: list }; }); }} placeholder='ФОП / ТОВ назва' style={{ ...inp, marginBottom: 6 }} />
              <textarea value={form.suppliersList[tab].details} onChange={e => { const v = e.target.value; setForm(f => { const list = [...f.suppliersList]; list[tab] = { ...list[tab], details: v }; return { ...f, suppliersList: list }; }); }} rows={3} placeholder={"ЄДРПОУ...\nр/р UA...\nБанк..."} style={{ ...inp, fontSize: 11, resize: "vertical" }} />
            </>
          )}
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={lbl}>📋 Отримувач (покупець)</label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginBottom: 6 }}>
            {form.clientsList.map((c, i) => (
              <div key={i} onClick={() => setCtab(i)} style={tabStyle(ctab === i, "#10b981")}>
                {c.name || `#${i + 1}`}
                <span onClick={e => { e.stopPropagation(); setForm(f => ({ ...f, clientsList: f.clientsList.filter((_, j) => j !== i) })); if (ctab >= i && ctab > 0) setCtab(ctab - 1); }} style={{ marginLeft: 6, cursor: "pointer", opacity: 0.6 }}>×</span>
              </div>
            ))}
            <button onClick={() => { setForm(f => ({ ...f, clientsList: [...f.clientsList, { name: "", details: "" }] })); setCtab(form.clientsList.length); }} style={{ padding: "5px 10px", fontSize: 11, fontWeight: 700, border: `1.5px dashed ${t.border}`, background: "transparent", color: t.textMuted, borderRadius: 6, cursor: "pointer" }}>+ Додати</button>
          </div>
          {form.clientsList[ctab] && (
            <>
              <input value={form.clientsList[ctab].name} onChange={e => { const v = e.target.value; setForm(f => { const list = [...f.clientsList]; list[ctab] = { ...list[ctab], name: v }; return { ...f, clientsList: list }; }); }} placeholder='ФОП / ТОВ назва' style={{ ...inp, marginBottom: 6 }} />
              <textarea value={form.clientsList[ctab].details} onChange={e => { const v = e.target.value; setForm(f => { const list = [...f.clientsList]; list[ctab] = { ...list[ctab], details: v }; return { ...f, clientsList: list }; }); }} rows={3} placeholder={"ЄДРПОУ...\nр/р UA...\nБанк..."} style={{ ...inp, fontSize: 11, resize: "vertical" }} />
            </>
          )}
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={lbl}>📧 Email для супровідного листа</label>
          <input value={form.welcomeEmail} onChange={e => setForm(f => ({ ...f, welcomeEmail: e.target.value }))} placeholder="client@example.com" style={inp} />
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={lbl}>🧾 Рахунки</label>
          {invoices.length === 0 ? (
            <div style={{ fontSize: 12, color: t.textMuted, padding: "10px 0" }}>Рахунків немає</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {invoices.map(inv => {
                const isPaid = inv.status === "paid";
                const periodLabel = inv.period_start ? new Date(inv.period_start).toLocaleDateString("uk-UA", { month: "2-digit", year: "2-digit" }) : "—";
                return (
                  <div key={inv.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", border: `1px solid ${t.border}`, borderRadius: 8, background: isPaid ? "rgba(16,185,129,0.05)" : "transparent" }}>
                    <div style={{ fontSize: 12 }}>
                      <span style={{ fontWeight: 600 }}>#{inv.invoice_number || inv.id}</span>
                      <span style={{ marginLeft: 10, color: t.textMuted }}>{periodLabel}</span>
                      <span style={{ marginLeft: 10, fontWeight: 600, color: "#6366f1" }}>{Number(inv.amount || 0).toLocaleString("uk-UA")} грн</span>
                    </div>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button onClick={async () => {
                        try {
                          const r = await apiFetch(`/api/projects/${projectId}/invoices/generate`, {
                            method: "POST",
                            body: JSON.stringify({
                              periodStart: new Date(inv.period_start).toISOString().slice(0, 10),
                              periodEnd: new Date(inv.period_end).toISOString().slice(0, 10),
                            }),
                          });
                          const d = await r.json();
                          if (d.html) { const w = window.open("", "_blank"); w.document.write(d.html); w.document.close(); }
                        } catch (e) { flash("Помилка: " + e.message); }
                      }} style={{ padding: "4px 10px", fontSize: 11, border: `1px solid ${t.border}`, background: "transparent", color: t.text, borderRadius: 6, cursor: "pointer" }} title="Відкрити рахунок">📄</button>
                      <button onClick={async () => {
                        try {
                          const r = await apiFetch(`/api/projects/${projectId}/invoices/generate`, {
                            method: "POST",
                            body: JSON.stringify({
                              periodStart: new Date(inv.period_start).toISOString().slice(0, 10),
                              periodEnd: new Date(inv.period_end).toISOString().slice(0, 10),
                            }),
                          });
                          const d = await r.json();
                          if (d.html) {
                            const w = window.open("", "_blank");
                            w.document.write(d.html); w.document.close();
                            const tryPrint = () => { try { w.focus(); w.print(); } catch {} };
                            if (w.document.readyState === "complete") setTimeout(tryPrint, 300);
                            else w.addEventListener("load", () => setTimeout(tryPrint, 300));
                          }
                        } catch (e) { flash("Помилка: " + e.message); }
                      }} style={{ padding: "4px 10px", fontSize: 11, border: `1px solid ${t.border}`, background: "transparent", color: "#10b981", borderRadius: 6, cursor: "pointer" }} title="Завантажити PDF">⬇</button>
                      <button onClick={async () => {
                        try {
                          const newStatus = isPaid ? "unpaid" : "paid";
                          const r = await apiFetch(`/api/projects/${projectId}/invoices/${inv.id}`, {
                            method: "PUT",
                            body: JSON.stringify({ status: newStatus }),
                          });
                          if (r.ok) loadInvoices();
                        } catch (e) { flash("Помилка: " + e.message); }
                      }} style={{ padding: "4px 12px", fontSize: 11, fontWeight: 700, border: "none", background: isPaid ? "rgba(16,185,129,0.15)" : "rgba(239,68,68,0.12)", color: isPaid ? "#10b981" : "#ef4444", borderRadius: 6, cursor: "pointer" }}>
                        {isPaid ? "Оплачено" : "Не оплачено"}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 20 }}>
          <button onClick={onClose} style={{ padding: "10px 20px", border: `1px solid ${t.border}`, background: "transparent", color: t.text, borderRadius: 8, fontSize: 13, cursor: "pointer" }}>Скасувати</button>
          <button onClick={save} disabled={saving} style={{ padding: "10px 24px", border: "none", background: "linear-gradient(135deg,#6366f1,#a855f7)", color: "#fff", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: saving ? "wait" : "pointer" }}>{saving ? "Збереження..." : "Зберегти"}</button>
        </div>
      </div>
    </div>
  );
}
